/**
 * Laatst bekende antwoorden van een handvol GET-endpoints, zodat de opstelling ook
 * zonder verbinding op het scherm staat.
 *
 * Dit is de native meerwaarde waar de App Store om vraagt (richtlijn 4.2): langs de
 * lijn is het netwerk vaak slecht, en juist dan wil je de opstelling zien.
 *
 * Bewust een expliciete allowlist en niet "alles cachen": een verouderde spelerslijst
 * is ongemakkelijk, een verouderd statistiekscherm is misleidend. Alleen wat je langs
 * de lijn nodig hebt staat erin.
 *
 * localStorage en niet @capacitor/preferences: dit moet op het web precies zo werken
 * (testbaar in jsdom), en het gaat om kleine JSON-antwoorden.
 */

const CACHE_PREFIX = 'offline_cache_';

/** Ouder dan dit tonen we niet meer - dan is "laatst bekend" eerder verwarrend. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface CachedEntry<T> {
  data: T;
  cachedAt: number;
}

/**
 * De endpoints die langs de lijn nodig zijn. De paden komen uit api.ts; zie de
 * bijbehorende methodes getPublicMatches, getPublicLineups, getTeamByPublicId,
 * getLineups, getLineup, getMatches en getTeam (waar getTeamPlayers op leunt).
 */
const CACHEABLE_ENDPOINTS: RegExp[] = [
  // De twee verzoeken die de app bij het opstarten doet. Zonder deze kwam een coach op
  // een veld zonder bereik op het inlogscherm terecht: /auth/me faalde, AuthContext kan
  // een netwerkfout niet van een 401 onderscheiden en concludeerde "geen sessie" - en
  // inloggen kan daar per definitie niet. Alles hierachter werd daarmee onbereikbaar,
  // hoe goed het ook gecachet was.
  //
  // Dat deze twee er eerst buiten stonden was een bewuste keuze, dus de omkering ook:
  // het gaat om je eigen naam en je eigen teams, ze staan op hetzelfde toestel als het
  // token in de Keychain, en clearCache() draait bij uitloggen en bij het opzeggen van
  // een account in een finally. Offline levert dit geen toegang op die je niet al had;
  // zodra er weer bereik is beslist de server alsnog, en een ingetrokken sessie geeft
  // dan een 401 die niet uit de cache wordt bediend.
  /^\/auth\/me$/,
  /^\/users\/me\/teams$/,
  // Publieke deelpagina: /public/teams/:publicId/:slug/matches
  /^\/public\/teams\/[^/?]+\/[^/?]+\/matches$/,
  // Publieke opstelling: /public/teams/:publicId/:slug/matches/:matchId/lineups
  /^\/public\/teams\/[^/?]+\/[^/?]+\/matches\/\d+\/lineups$/,
  // Publiek team: /teams/public/:publicId/:slug
  /^\/teams\/public\/[^/?]+\/[^/?]+$/,
  // Coach: opstellingen van een wedstrijd en losse opstelling
  /^\/lineups(\?matchId=\d+)?$/,
  /^\/lineups\/\d+$/,
  // Coach: de posities van een formatie. Zonder deze bleef de opstelling offline leeg:
  // /lineups levert wel de spelers per positie, maar NewLineupManager bouwt het veld op
  // uit de formatiepositie en haalt die apart op. Faalde die ene GET, dan gaf
  // generatePositions een lege lijst terug en was er niets te zien.
  /^\/formations\/\d+\/positions$/,
  // Coach: de formaties van het team. FormationsContext heeft een eigen cache van 30
  // minuten en valt daarna terug op de API; zonder deze regel is de formatiekeuze na een
  // half uur zonder bereik leeg.
  /^\/formations(\?teamId=\d+)?$/,
  // Coach: wedstrijden, eventueel op team gefilterd
  /^\/matches(\?teamSlug=[^&]*)?$/,
  // Coach: één wedstrijd. Zonder dit werkt de lijst offline wel en de detailpagina
  // niet, en juist daar staat de opstelling - precies wat richtlijn 4.2 moet aantonen.
  /^\/matches\/\d+$/,
  // Coach: wie er is afgemeld voor die wedstrijd. Hoort bij hetzelfde scherm en de
  // opstellingsbewerker waarschuwt ermee over afwezige spelers.
  /^\/matches\/\d+\/availability$/,
  // Coach: team inclusief spelers - getTeamPlayers leidt zijn spelers hieruit af
  /^\/teams\/\d+$/,
  // Coach: één speler. Naam, rugnummer en positie - hetzelfde soort gegevens als
  // /teams/:id hierboven. De statistieken op datzelfde scherm blijven er bewust buiten
  // (zie de toelichting hierboven); SpelerDetail vangt die apart af en toont de rest
  // gewoon, dus offline mist alleen het statistiekblok.
  /^\/players\/\d+$/,
];

export function isCacheable(endpoint: string, method?: string): boolean {
  // Alleen leesverkeer; een POST cachen zou een schrijfactie stilletjes laten slagen.
  if (method && method.toUpperCase() !== 'GET') return false;
  return CACHEABLE_ENDPOINTS.some((pattern) => pattern.test(endpoint));
}

function storageKey(endpoint: string): string {
  return `${CACHE_PREFIX}${endpoint}`;
}

export function readCache<T>(endpoint: string): CachedEntry<T> | null {
  try {
    const raw = localStorage.getItem(storageKey(endpoint));
    if (!raw) return null;

    const entry = JSON.parse(raw) as CachedEntry<T>;
    if (typeof entry?.cachedAt !== 'number') return null;

    if (Date.now() - entry.cachedAt > MAX_AGE_MS) {
      localStorage.removeItem(storageKey(endpoint));
      return null;
    }

    return entry;
  } catch {
    // Kapotte of onleesbare cache is geen fout: dan is er gewoon niets bewaard.
    return null;
  }
}

export function writeCache<T>(endpoint: string, data: T): void {
  // undefined komt voor bij 204-antwoorden en heeft niets in de cache te zoeken.
  if (data === undefined) return;

  try {
    const entry: CachedEntry<T> = { data, cachedAt: Date.now() };
    localStorage.setItem(storageKey(endpoint), JSON.stringify(entry));
  } catch (error) {
    // Volle storage mag een geslaagd verzoek niet laten omvallen.
    console.warn('offlineCache: kon antwoord niet bewaren', error);
  }
}

/**
 * De banner hangt niet aan een scherm maar aan het verkeer: elk scherm laadt zijn
 * eigen data via api.ts, dus daar is ook het enige punt waar "dit kwam uit de cache"
 * bekend is. Zelfde CustomEvent-idioom als 'auth-failure' en 'team-switched'.
 */
export const CACHE_SERVED_EVENT = 'offline-cache-served';
export const CACHE_FRESH_EVENT = 'offline-cache-fresh';

export function notifyServedFromCache(cachedAt: number): void {
  window.dispatchEvent(new CustomEvent(CACHE_SERVED_EVENT, { detail: { cachedAt } }));
}

export function notifyFreshData(): void {
  window.dispatchEvent(new CustomEvent(CACHE_FRESH_EVENT));
}

/** Wist de cache; hoort bij uitloggen, want de inhoud is teamgebonden. */
export function clearCache(): void {
  try {
    const keys = Object.keys(localStorage).filter((key) => key.startsWith(CACHE_PREFIX));
    keys.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn('offlineCache: kon cache niet wissen', error);
  }
}
